import { useState } from 'react';
import { Download, FileText, Calendar } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

interface DateRange {
  startDate: string;
  endDate: string;
}

export default function TaxReportExport() {
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);

  const exportDetailedReport = async () => {
    setLoading(true);
    try {
      const getTaxReport = httpsCallable(functions, 'getTaxReport');
      const result = await getTaxReport({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      
      const data = result.data as any;
      
      if (data.success) {
        generateCSV(data.payments, data.summary);
      }
    } catch (error) {
      console.error('Error exporting tax report:', error);
      alert('เกิดข้อผิดพลาดในการ Export รายงาน');
    } finally {
      setLoading(false);
    }
  };

  const generateCSV = (payments: any[], summary: any) => {
    const headers = [
      'วันที่',
      'เลขที่รายการ',
      'ชื่อลูกค้า',
      'อีเมล',
      'แพ็กเกจ',
      'ราคาก่อน VAT',
      'VAT 7%',
      'ราคารวม VAT',
      'สถานะ',
      'หมายเหตุ'
    ];

    const rows = payments.map(payment => [
      new Date(payment.createdAt).toLocaleDateString('th-TH'),
      payment.id,
      payment.userName || '-',
      payment.userEmail || '-',
      payment.planName || '-',
      (payment.amount / 1.07).toFixed(2), // ราคาก่อน VAT (เตรียมไว้)
      ((payment.amount / 1.07) * 0.07).toFixed(2), // VAT 7% (เตรียมไว้)
      payment.amount.toFixed(2),
      payment.status,
      payment.note || '-'
    ]);

    // Add summary rows
    rows.push([]);
    rows.push(['สรุป']);
    rows.push(['จำนวนรายการทั้งหมด', summary.totalCount.toString()]);
    rows.push(['รายได้รวม (ก่อน VAT)', (summary.totalRevenue / 1.07).toFixed(2)]);
    rows.push(['VAT 7%', ((summary.totalRevenue / 1.07) * 0.07).toFixed(2)]);
    rows.push(['รายได้รวม (รวม VAT)', summary.totalRevenue.toFixed(2)]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `tax_report_${dateRange.startDate}_to_${dateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const setQuickRange = (type: 'thisMonth' | 'lastMonth' | 'thisYear' | 'lastYear') => {
    const now = new Date();
    let start: Date;
    let end: Date;

    switch (type) {
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'thisYear':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
      case 'lastYear':
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear() - 1, 11, 31);
        break;
    }

    setDateRange({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-cyan-50">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-textDark">รายงานภาษี</h2>
            <p className="text-sm text-textLight">Export รายงานรายได้สำหรับยื่นภาษี</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Quick Range Buttons */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            <Calendar className="w-4 h-4 inline mr-2" />
            เลือกช่วงเวลาด่วน:
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button
              onClick={() => setQuickRange('thisMonth')}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
            >
              เดือนนี้
            </button>
            <button
              onClick={() => setQuickRange('lastMonth')}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
            >
              เดือนที่แล้ว
            </button>
            <button
              onClick={() => setQuickRange('thisYear')}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
            >
              ปีนี้
            </button>
            <button
              onClick={() => setQuickRange('lastYear')}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
            >
              ปีที่แล้ว
            </button>
          </div>
        </div>

        {/* Custom Date Range */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              วันที่เริ่มต้น
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              วันที่สิ้นสุด
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">ข้อมูลในรายงาน:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• รายละเอียดการชำระเงินทั้งหมด</li>
            <li>• ราคาก่อน VAT, VAT 7%, และราคารวม (เตรียมไว้สำหรับอนาคต)</li>
            <li>• ข้อมูลลูกค้า (ชื่อ, อีเมล)</li>
            <li>• แพ็กเกจที่ซื้อ</li>
            <li>• สรุปยอดรวมทั้งหมด</li>
          </ul>
        </div>

        {/* Export Button */}
        <button
          onClick={exportDetailedReport}
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>กำลัง Export...</span>
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              <span>Export รายงานภาษี (CSV)</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
